import { getUserAge, getIsInvalidDayInMonth } from '@gif/shared-global';
import {
  isLettersAndNumbersOnly,
  isNotProfane,
  isNotWithinRange,
} from '../../../utils';
import { FormValueErrors, FormValues } from './AdditionalInformation.types';
import { translate } from '../../../configs';

interface IsFormValid {
  isValid: boolean;
  errors: FormValueErrors;
}

export interface SupportObj {
  minYear: number;
  currentYear: number;
  daysInMonth: number;
  monthsInYear: number;
  isCanada: boolean;
}

const youngestRecordedPerson = 13;
const oldestRecordedPerson = 122;
const yearsLength = 4;
const daysInMonth = 31;
const monthsInYear = 12;

export const isAddInfoFormValid = (formValObj: FormValues): IsFormValid => {
  const { nickName, birthDay, birthMonth, birthYear, country } = formValObj;
  const birthDayNumber = Number(birthDay);
  const birthMonthNumber = Number(birthMonth);
  const birthYearNumber = Number(birthYear);
  const currentDate = new Date();
  const currentYear = currentDate.getUTCFullYear();
  const minYear = currentYear - oldestRecordedPerson;
  const maxYear = currentYear - youngestRecordedPerson;
  const countryCode = country.country?.countryCode;

  // Validate nickname
  if (!nickName || isNotWithinRange(nickName.length, 6, 15)) {
    return {
      isValid: false,
      errors: {
        nickName: translate('nicknameLengthError'),
      },
    };
  } else if (!isLettersAndNumbersOnly(nickName)) {
    return {
      isValid: false,
      errors: {
        nickName: translate('nicknameAlphanumericError'),
      },
    };
  } else if (!isNotProfane(nickName)) {
    return {
      isValid: false,
      errors: {
        nickName: translate('chooseAnotherNickname'),
      },
    };
  }

  // Validate month
  if (
    !birthMonth ||
    isNaN(birthMonthNumber) ||
    isNotWithinRange(birthMonthNumber, 1, monthsInYear)
  ) {
    return {
      isValid: false,
      errors: {
        birthMonth: translate('enterValidMonth'),
      },
    };
  }

  // Validate day
  if (
    !birthDay ||
    isNaN(birthDayNumber) ||
    isNotWithinRange(birthDayNumber, 1, daysInMonth)
  ) {
    return {
      isValid: false,
      errors: {
        birthDay: translate('enterValidDay'),
      },
    };
  }

  // Validate year
  if (
    !birthYear ||
    birthYear.length !== yearsLength ||
    isNaN(birthYearNumber) ||
    birthYearNumber > currentYear ||
    birthYearNumber > maxYear ||
    isNotWithinRange(birthYearNumber, minYear, currentYear)
  ) {
    return {
      isValid: false,
      errors: {
        birthYear: translate('enterValidYear'),
      },
    };
  }

  // Check day is valid for given month
  if (
    getIsInvalidDayInMonth(birthMonthNumber, birthDayNumber, birthYearNumber)
  ) {
    return {
      isValid: false,
      errors: {
        birthDay: translate('enterValidDate'),
      },
    };
  }

  // Validate country
  if (!countryCode) {
    return {
      isValid: false,
      errors: {
        country: translate('countryIsRequired'),
      },
    };
  }

  // Validate province
  if (countryCode?.toLowerCase() === 'ca' && !country.province?.provinceCode) {
    return {
      isValid: false,
      errors: {
        country: translate('provinceIsRequired'),
      },
    };
  }

  // If it has reached here, there were no errors yet
  const userBirthDate = new Date(
    birthYearNumber,
    birthMonthNumber - 1,
    birthDayNumber
  );
  const userAge = getUserAge(userBirthDate);
  const isUserInvalidAge =
    userAge < youngestRecordedPerson ||
    userAge > oldestRecordedPerson ||
    userBirthDate > currentDate;

  // Check min 13 years old
  if (isUserInvalidAge) {
    return {
      isValid: false,
      errors: {
        birthYear: translate('minMaxAgeError', {
          min: youngestRecordedPerson,
          max: oldestRecordedPerson,
        }),
      },
    };
  }

  return {
    isValid: true,
    errors: {},
  };
};
